/**
 * Check approximate equality of two floating point numbers using a set threshold.
 */
export function compareFloatEqual(n1: number, n2: number, threshold = 1e-9) {
  return Math.abs(n1 - n2) < threshold;
}

/**
 * Find the best fractional representation of a number withing set error bounds,
 * up to a specified maximum denominator value.
 *
 * Returns `null` if no such fractional representation is found.
 */
export function decimalToFractionExhaustive(
  n: number,
  maxDenominator: number,
  maxError: number
): [number, number] | null {
  for (let den = 1; den <= maxDenominator; den++) {
    const num = Math.round(n * den);
    const error = Math.abs(n - num / den);
    if (error <= maxError) {
      return [num, den];
    }
  }
  return null;
}

/**
 * Format a number as a fraction/multiple of PI.
 *
 * When the number is not representable as a fraction up to specified error,
 * then it is formatted using its decimal representation.
 */
export function formatPiFraction(value: number, maxError = 0.005) {
  const valuePi = value / Math.PI;
  const fraction = decimalToFractionExhaustive(valuePi, 10, maxError);
  if (fraction == null) {
    // decimal representation
    const decimal =
      Math.floor(valuePi * 100) === 0 ? valuePi.toFixed(0) : valuePi.toFixed(2);
    return `${decimal}π`;
  } else {
    const [num, den] = fraction;
    // fraction representation
    if (den === 1 && num === 1) {
      return 'π';
    }
    if (den === 1) {
      return `${num}π`;
    }
    if (num === 1) {
      return `π/${den}`;
    }
    return `${num}π/${den}`;
  }
}
